/* Birthday Automation — shared UI primitives.
 * Styles are copied 1:1 from the existing Nexus design tokens / Card / KPI so the
 * tab is visually identical. No new UI library, inline styles only.
 */

export const C = {
  accent: "#FF5A00", accentL: "#FFF0ED", bg: "#F5F2EE",
  white: "#FFFFFF", border: "#E8E4DF", text: "#1A1A1A",
  muted: "#9B9792", sub: "#6B6B6B",
  success: "#15803D", error: "#DC2626",
};

export function Card({ children, style = {} }) {
  return (
    <div style={{ background: C.white, borderRadius: 10, padding: 16, border: `1px solid ${C.border}`, ...style }}>
      {children}
    </div>
  );
}

/* KPI card — identical to App.js KPI (read-only: onClick omitted). */
export function KPI({ label, value, sub, color }) {
  const accent = color || C.accent;
  return (
    <div style={{ background: C.white, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent, letterSpacing: "-.5px", margin: "4px 0 2px" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted }}>{sub}</div>}
    </div>
  );
}

/* Soft-tint pill — matches the existing Nexus sentiment/channel badges in App.js
 * (fontSize 10, padding 2px 7px, borderRadius 8). */
export function Badge({ label, bg = "#F4F2EE", color = C.sub, title }) {
  return (
    <span title={title} style={{ display: "inline-block", fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 8, background: bg, color }}>
      {label}
    </span>
  );
}

/* Titled card section with an optional right slot. */
export function Section({ title, sub, right, children, style = {} }) {
  return (
    <Card style={{ ...style }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{title}</div>
          {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{sub}</div>}
        </div>
        {right && <div>{right}</div>}
      </div>
      {children}
    </Card>
  );
}

export function Banner({ tone = "info", children }) {
  const tones = {
    info: { bg: "#E3F2FD", color: "#1565C0" },
    warn: { bg: "#FFF8E1", color: "#B7791F" },
    danger: { bg: "#FDECEA", color: C.error },
    neutral: { bg: "#F9F8F7", color: C.sub },
    success: { bg: "#E8F5E9", color: C.success },
  };
  const t = tones[tone] || tones.info;
  return (
    <div style={{ background: t.bg, color: t.color, borderRadius: 8, padding: "10px 14px", fontSize: 12.5, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh", color: C.muted, fontSize: 13 }}>
      {label}
    </div>
  );
}

export function EmptyState({ title, sub }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.sub, marginBottom: 4 }}>{title}</div>
      {sub && <div style={{ fontSize: 12.5 }}>{sub}</div>}
    </div>
  );
}

/* Form / button helpers — copied 1:1 from the existing Nexus inputs & buttons
 * in App.js (Admin Settings form, Offer Creation, Revoke). No new visual style. */
export const labelStyle = { display: "block", fontSize: 11, fontWeight: 500, color: C.sub, marginBottom: 6, textTransform: "uppercase" };
export const inputStyle = { width: "100%", padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
export const btnPrimary = { padding: "8px 16px", background: C.accent, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };
export const btnSecondary = { padding: "8px 14px", background: "#F4F2EE", color: C.text, border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" };
export const btnDangerText = { background: "none", border: "none", color: C.error, cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "8px 6px" };

/* Table cell helpers — mirror the existing Nexus tables in App.js:
 * tinted header row (#F9F8F7) + lighter row dividers (#F4F2EE). */
export const theadRowStyle = { background: "#F9F8F7", borderBottom: `1px solid ${C.border}` };
export const thStyle = { textAlign: "left", fontSize: 10, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: .5, padding: "10px 12px", whiteSpace: "nowrap" };
export const tdStyle = { fontSize: 12, color: C.text, padding: "9px 12px", borderBottom: "1px solid #F4F2EE", verticalAlign: "middle" };
